import { IntegrityMonitorRulesGridBaseDataProvider } from './IntegrityMonitorRulesGridBaseDataProvider';
import {
  BooleanOption,
  Column,
  ColumnKeys,
  DISPLAYED_COLUMNS_RULES,
  DISPLAYED_COLUMNS_RULES_NO_ACTIONS,
  IM_RULES_DISPLAYED_FILTERS,
} from '../types';
import { ColumnDetails, ColumnType } from '../../../../grid/types/common';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, ViewConfiguration } from '../../../../grid/gridView/types';
import { DefaultSmartViewId, ViewType } from '../../integrityMonitorRulesSmartViews/types';
import {
  getIntegrityMonitorOsTypes,
  getIntegrityMonitorSeverityValues,
  getIntegrityMonitorTypeValues,
} from '../../../common/utils/types';
import { sanitizeObject } from '../../../../utils/sanitization.utils';

export class IntegrityMonitorRulesGridGzDataProvider extends IntegrityMonitorRulesGridBaseDataProvider {
  getRulesViewColumnDetails(
    disableRuleTypeFilter = false,
    noActions = false
  ): Array<ColumnDetails<Column, ColumnKeys>> {
    const translateFn = this.dependencies.translateFn;

    if (noActions) {
      return [
        this.getRuleNameColumnDetails(translateFn),
        this.getDescriptionColumnDetails(translateFn),
        this.getEntityTypeColumnDetails(translateFn),
        this.getPlatformColumnDetails(translateFn),
        this.getSeverityColumnDetails(translateFn),
        this.getRuleTypeColumnDetails(translateFn, disableRuleTypeFilter),
        this.getCreatedOnColumnDetails(translateFn),
        this.getStatusColumnDetails(translateFn),
        this.getLastModifiedOnColumnDetails(translateFn),
        this.getRealTimeMonitoringColumnDetails(translateFn),
        this.getVersionColumnDetails(translateFn),
      ];
    }

    return [
      this.getRuleNameColumnDetails(translateFn),
      this.getDescriptionColumnDetails(translateFn),
      this.getEntityTypeColumnDetails(translateFn),
      this.getPlatformColumnDetails(translateFn),
      this.getSeverityColumnDetails(translateFn),
      this.getRuleTypeColumnDetails(translateFn, disableRuleTypeFilter),
      this.getCreatedOnColumnDetails(translateFn),
      this.getStatusColumnDetails(translateFn),
      this.getLastModifiedOnColumnDetails(translateFn),
      this.getRealTimeMonitoringColumnDetails(translateFn),
      this.getVersionColumnDetails(translateFn),
      this.getConfigStatusColumnDetails(),
      this.getInlineActionsColumnDetails(),
    ];
  }

  getAllRulesDefaultViewProps(): ViewConfiguration<Column, ColumnKeys, ViewType> {
    const translateFn = this.dependencies.translateFn;

    return {
      id: DefaultSmartViewId.ALL_RULES,
      name: translateFn('All rules'),
      type: ViewType.ALL_RULES,
      filters: IM_RULES_DISPLAYED_FILTERS,
      displayedColumns: DISPLAYED_COLUMNS_RULES,
      compactView: false,
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: DEFAULT_PAGE_NUMBER,
    };
  }

  getDefaultOsRulesDefaultViewProps(): ViewConfiguration<Column, ColumnKeys, ViewType> {
    const translateFn = this.dependencies.translateFn;
    return {
      id: DefaultSmartViewId.DEFAULT_OS_RULES,
      name: translateFn('Default OS rules'),
      type: ViewType.DEFAULT_OS_RULES,
      filters: IM_RULES_DISPLAYED_FILTERS,
      displayedColumns: DISPLAYED_COLUMNS_RULES,
      compactView: false,
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: DEFAULT_PAGE_NUMBER,
    };
  }

  getDefaultApplicationRulesDefaultViewProps(): ViewConfiguration<Column, ColumnKeys, ViewType> {
    const translateFn = this.dependencies.translateFn;

    return {
      id: DefaultSmartViewId.DEFAULT_APPLICATION_RULES,
      name: translateFn('Default application rules'),
      type: ViewType.DEFAULT_APPLICATION_RULES,
      filters: IM_RULES_DISPLAYED_FILTERS,
      displayedColumns: DISPLAYED_COLUMNS_RULES,
      compactView: false,
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: DEFAULT_PAGE_NUMBER,
    };
  }

  getCustomRulesDefaultViewProps(): ViewConfiguration<Column, ColumnKeys, ViewType> {
    const translateFn = this.dependencies.translateFn;

    return {
      id: DefaultSmartViewId.CUSTOM_RULES,
      name: translateFn('Custom rules'),
      type: ViewType.CUSTOM_RULES,
      filters: IM_RULES_DISPLAYED_FILTERS,
      displayedColumns: DISPLAYED_COLUMNS_RULES,
      compactView: false,
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: DEFAULT_PAGE_NUMBER,
    };
  }

  getRuleSetDefaultViewProps(): ViewConfiguration<Column, ColumnKeys, ViewType> {
    return {
      id: '', // dynamically completed with the rule set's _id
      name: '', // dynamically completed with the rule set's ruleSetName
      type: ViewType.RULE_SET,
      filters: IM_RULES_DISPLAYED_FILTERS,
      displayedColumns: DISPLAYED_COLUMNS_RULES_NO_ACTIONS,
      compactView: false,
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: DEFAULT_PAGE_NUMBER,
    };
  }

  public async getRulesCountForRuleSet(ruleSetId: string): Promise<number> {
    try {
      const response = await this.dependencies.api.clients.IntegrityMonitorRuleSets.getRulesCountForRuleSet(
        ruleSetId
      ).toPromise();

      if (!response || !response.success) {
        throw new Error('Invalid response of getRulesCountForRuleSet');
      }

      return sanitizeObject(response.count);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  public async unassignRulesFromRuleSet(ruleIds: Array<string>, ruleSetId: string): Promise<Object | null> {
    try {
      const response = await this.dependencies.api.clients.IntegrityMonitorRuleSets.unassignRulesFromRuleSet(
        ruleIds,
        ruleSetId
      ).toPromise();

      return sanitizeObject(response);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  private getRuleNameColumnDetails(translateFn): ColumnDetails<Column, ColumnKeys> {
    return {
      column: Column.RULE_NAME,
      key: ColumnKeys.RULE_NAME,
      columnName: translateFn('Rule name'),
      type: ColumnType.STRING,
      removable: false,
    };
  }

  private getDescriptionColumnDetails(translateFn): ColumnDetails<Column, ColumnKeys> {
    return {
      column: Column.DESCRIPTION,
      key: ColumnKeys.DESCRIPTION,
      columnName: translateFn('Description'),
      type: ColumnType.STRING,
      removable: true,
      filteringDisabled: true,
    };
  }

  private getEntityTypeColumnDetails(translateFn): ColumnDetails<Column, ColumnKeys> {
    return {
      column: Column.ENTITY_TYPE,
      key: ColumnKeys.ENTITY_TYPE,
      columnName: translateFn('Entity type'),
      type: ColumnType.ENUM,
      values: getIntegrityMonitorTypeValues(translateFn),
      removable: false,
    };
  }

  private getPlatformColumnDetails(translateFn): ColumnDetails<Column, ColumnKeys> {
    return {
      column: Column.PLATFORM,
      key: ColumnKeys.PLATFORM,
      columnName: translateFn('OS'),
      type: ColumnType.ENUM,
      values: getIntegrityMonitorOsTypes(translateFn)
        .filter(os => os.implemented)
        .map(os => {
          return {
            label: os.label,
            value: os.value,
          };
        }),
      removable: false,
    };
  }

  private getSeverityColumnDetails(translateFn): ColumnDetails<Column, ColumnKeys> {
    return {
      column: Column.SEVERITY,
      key: ColumnKeys.SEVERITY,
      columnName: translateFn('Severity'),
      type: ColumnType.ENUM,
      values: getIntegrityMonitorSeverityValues(translateFn),
      removable: false,
      sortable: true,
    };
  }

  private getRuleTypeColumnDetails(translateFn, disableRuleTypeFilter: boolean): ColumnDetails<Column, ColumnKeys> {
    return {
      column: Column.RULE_TYPE,
      key: ColumnKeys.RULE_TYPE,
      columnName: translateFn('Rule type'),
      type: ColumnType.ENUM,
      values: [
        { label: translateFn('Default'), value: BooleanOption.POSITIVE },
        { label: translateFn('Custom'), value: BooleanOption.NEGATIVE },
      ],
      removable: true,
      filteringDisabled: disableRuleTypeFilter,
    };
  }

  private getCreatedOnColumnDetails(translateFn): ColumnDetails<Column, ColumnKeys> {
    return {
      column: Column.CREATED_ON,
      key: ColumnKeys.CREATED_ON,
      columnName: translateFn('Created on'),
      type: ColumnType.DATE,
      removable: true,
      filteringDisabled: true,
      sortable: true,
    };
  }

  private getLastModifiedOnColumnDetails(translateFn): ColumnDetails<Column, ColumnKeys> {
    return {
      column: Column.LAST_MODIFIED_ON,
      key: ColumnKeys.LAST_MODIFIED_ON,
      columnName: translateFn('Last modified on'),
      type: ColumnType.DATE,
      removable: true,
      filteringDisabled: true,
    };
  }

  private getStatusColumnDetails(translateFn): ColumnDetails<Column, ColumnKeys> {
    return {
      column: Column.STATUS,
      key: ColumnKeys.STATUS,
      columnName: translateFn('Status'),
      type: ColumnType.NUMBER,
      removable: true,
      filteringDisabled: true,
      sortable: true,
    };
  }

  private getRealTimeMonitoringColumnDetails(translateFn): ColumnDetails<Column, ColumnKeys> {
    return {
      column: Column.REAL_TIME_MONITORING,
      key: ColumnKeys.REAL_TIME_MONITORING,
      columnName: translateFn('Real-time monitoring'),
      type: ColumnType.ENUM,
      values: [
        { label: translateFn('On#Active'), value: BooleanOption.POSITIVE },
        { label: translateFn('Off'), value: BooleanOption.NEGATIVE },
      ],
      removable: false,
      filteringDisabled: true,
      sortable: true,
    };
  }

  private getVersionColumnDetails(translateFn): ColumnDetails<Column, ColumnKeys> {
    return {
      column: Column.VERSION,
      key: ColumnKeys.VERSION,
      columnName: translateFn('Version'),
      type: ColumnType.NUMBER,
      removable: true,
      filteringDisabled: true,
      sortable: true,
    };
  }

  /**
   * The configuration status column - shows an icon if necessary. Keep this column as the penultimate.
   * @private
   */
  private getConfigStatusColumnDetails(): ColumnDetails<Column, ColumnKeys> {
    return {
      column: Column.CONFIG_STATUS,
      key: ColumnKeys.CONFIG_STATUS,
      columnName: '',
      type: ColumnType.ACTION,
      removable: false,
      filteringDisabled: true,
      width: '40px',
    };
  }

  /**
   * The custom inline actions column - there is the need to render different actions for each line, which is not
   * possible at the moment with the grid's inline row actions. Keep this column as the last.
   * @private
   */
  private getInlineActionsColumnDetails(): ColumnDetails<Column, ColumnKeys> {
    return {
      column: Column.INLINE_ACTIONS,
      key: ColumnKeys.INLINE_ACTIONS,
      columnName: '',
      type: ColumnType.ACTION,
      removable: false,
      filteringDisabled: true,
      width: '48px',
    };
  }
}
